"use client";
import React, { useState, useRef } from "react";
import {
  alpha,
  Box,
  useMediaQuery,
  useTheme,
  IconButton,
  Dialog,
} from "@mui/material";
import "./video.css";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";

interface VideoProps {
  homeVideoBanner: any;
}

const Video: React.FC<VideoProps> = ({ homeVideoBanner }) => {
  const [show, setShow] = useState(true);
  const [openDialog, setOpenDialog] = useState(false); // For Dialog state
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleDialogOpen = () => {
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  return (
    <>
      {!openDialog ? (
        <Box
          role="complementary"
          aria-label="Promotional video preview"
          sx={{
            position: "fixed",
            bottom: {
              xs: "calc(96px + env(safe-area-inset-bottom, 0px))",
              sm: 24,
            },
            left: { xs: 12, sm: 24 },
            zIndex: 1005,
            display: show ? "block" : "none",
            borderRadius: "16px",
            overflow: "hidden",
            border: `1px solid ${alpha("#1f7a4d", 0.22)}`,
            boxShadow: "0 16px 44px rgba(18, 53, 36, 0.22)",
            background: "#0b1710",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 20px 52px rgba(18, 53, 36, 0.28)",
            },
          }}
        >
          <IconButton
            size="small"
            onClick={() => setShow(false)}
            aria-label="Close video preview"
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              zIndex: 1011,
              color: "#fff",
              bgcolor: alpha("#123524", 0.72),
              backdropFilter: "blur(8px)",
              width: 30,
              height: 30,
              "&:hover": { bgcolor: alpha("#1f7a4d", 0.9) },
            }}
          >
            <CloseIcon sx={{ fontSize: "1.1rem" }} />
          </IconButton>

          <video
            id="floating-preview-video"
            ref={videoRef}
            autoPlay
            muted
            loop
            onClick={handleDialogOpen}
            onContextMenu={(e) => e.preventDefault()}
            disablePictureInPicture
            controlsList="nodownload noremoteplayback noplaybackrate"
            controls={false}
            playsInline
            style={{
              borderRadius: "14px",
              width: isSmallScreen ? "132px" : "188px",
              height: isSmallScreen ? "236px" : "328px",
              objectFit: "cover",
              cursor: "pointer",
              display: "block",
            }}
          >
            <source
              src={homeVideoBanner?.bannerFile?.secureHttpPath}
              type="video/mp4"
            />
          </video>
        </Box>
      ) : (
        <Dialog
          open={openDialog}
          onClose={handleDialogClose}
          maxWidth="xs"
          aria-label="SalonTym video"
          PaperProps={{
            sx: {
              borderRadius: { xs: 0, sm: "20px" },
              overflow: "hidden",
              border: { xs: "none", sm: `1px solid ${alpha("#1f7a4d", 0.15)}` },
              boxShadow: {
                xs: "none",
                sm: "0 24px 56px rgba(18, 53, 36, 0.18)",
              },
              background: "#0b1710",
            },
          }}
          sx={{
            background: alpha("#0b1710", 0.55),
            backdropFilter: "blur(6px)",
            "& .MuiDialog-paper": {
              maxWidth: { xs: "-webkit-fill-available", sm: "370px" },
              maxHeight: "100%",
            },
            "@media (max-width: 600px)": {
              "& .MuiDialog-paper": {
                width: "100%",
                height: "100%",
                maxHeight: "100%",
                margin: 0,
              },
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <IconButton
              onClick={handleDialogClose}
              aria-label="Close full video"
              sx={{
                position: "absolute",
                top: 16,
                left: 16,
                color: "#fff",
                background: alpha("#123524", 0.88),
                borderRadius: "50%",
                zIndex: 1012,
                padding: "8px",
                border: `1px solid ${alpha("#fff", 0.2)}`,
                "&:hover": {
                  background: alpha("#1f7a4d", 0.95),
                },
              }}
            >
              <KeyboardBackspaceIcon sx={{ fontSize: "1.25rem" }} />
            </IconButton>

            <video
              id="floating-dialog-video"
              ref={videoRef}
              autoPlay
              muted={false}
              loop
              controls={false} // Disable video controls
              onContextMenu={(e) => e.preventDefault()} // Prevent right-click menu
              disablePictureInPicture
              controlsList="nodownload noremoteplayback noplaybackrate" // Hide controls
              playsInline
              style={{
                width: "auto",
                height: "100%",
                objectFit: isSmallScreen ? "cover" : "contain", // Show video fully inside the dialog
              }}
            >
              <source
                src={homeVideoBanner?.videoFile?.secureHttpPath}
                type="video/mp4"
              />
            </video>
          </Box>
        </Dialog>
      )}
    </>
  );
};

export default Video;
